import type { ReactElement } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Link } from '@hivebrite/design-system/Link';
import { Loader } from '@hivebrite/design-system/Loader';

import LoadingBox from 'hb-builder/components/common/LoadingComponents/LoadingBox';
import { getIsExternalLink, getLink } from 'hb-builder/utils/links';
import type {
  PartnersItem,
  PartnersItemAttributes,
} from 'hb-react/backoffice/types/builder/itemAttributes/partnersItemAttributes';

import { PARTNERS_LOGOS } from '../constants';

const LogoWithLink = ({
  canOpenInNewTab,
  children,
  partnerImageUrl,
}: {
  canOpenInNewTab?: boolean;
  children: ReactElement;
  partnerImageUrl: PartnersItemAttributes['partnerImageUrl'];
}) => {
  if (!partnerImageUrl) {
    return children;
  }

  const domain = window.location.hostname;

  const linkProps = {
    forceDefaultLink: getIsExternalLink(partnerImageUrl, domain),
    target: canOpenInNewTab ? '_blank' : '_self',
    to: getLink(partnerImageUrl || '#', domain),
  };

  return (
    <Link
      {...linkProps}
      rel="noreferrer"
      style={{ height: '100%', width: '100%' }}
    >
      {children}
    </Link>
  );
};

interface PartnerUIProps {
  index: PartnersItem['index'];
  isLoadingSrc?: PartnersItemAttributes['isLoadingSrc'];
  logoBackground?: PartnersItemAttributes['logoBackground'];
  openInNewTab?: PartnersItemAttributes['openInNewTab'];
  padding?: { _: number; m: number };
  partnerImage: PartnersItemAttributes['partnerImage'];
  partnerImageAlt?: PartnersItem['partnerImageAlt'];
  partnerImageUrl?: PartnersItem['partnerImageUrl'];
  subdomainNetworkUrl?: string;
}

const PartnerUI = ({
  index,
  isLoadingSrc,
  logoBackground,
  openInNewTab,
  padding,
  partnerImage,
  partnerImageAlt,
  partnerImageUrl,
  subdomainNetworkUrl,
}: PartnerUIProps) => (
  <LogoWithLink
    canOpenInNewTab={openInNewTab}
    partnerImageUrl={partnerImageUrl}
  >
    {partnerImage ? (
      <LogoWrapper bg={logoBackground} p={padding} dataTestId="partner-ui-logo">
        <StyledImage $image={partnerImage}>
          {isLoadingSrc && (
            <LoadingBox>
              <Loader size={40} />
            </LoadingBox>
          )}
          {partnerImageAlt && <span role="img" aria-label={partnerImageAlt} />}
        </StyledImage>
      </LogoWrapper>
    ) : (
      <LogoWrapper
        bg={logoBackground}
        p={padding}
        dataTestId="partner-ui-placeholder"
      >
        <StyledImage
          $image={
            subdomainNetworkUrl
              ? `${subdomainNetworkUrl}${PARTNERS_LOGOS[index || 0]}`
              : PARTNERS_LOGOS[index || 0]
          }
        >
          {isLoadingSrc && (
            <LoadingBox>
              <Loader size={40} />
            </LoadingBox>
          )}
          <span role="img" aria-label="logo placeholder" />
        </StyledImage>
      </LogoWrapper>
    )}
  </LogoWithLink>
);

export default PartnerUI;

const LogoWrapper = styled(Box).attrs(() => ({
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
}))`
  ${({ theme }) =>
    css`
      border-radius: ${theme.themeVariables.globalBorderRadius}px;
    `}
`;

interface StyledImageProps {
  $image: string;
}

const StyledImage = styled(Box).attrs(() => ({
  width: '100%',
  height: '42px',
}))<StyledImageProps>`
  background-image: ${({ $image }) => `url(${$image})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  position: relative;
`;
